import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../../contexts/AppContext';
import GodRays from './GodRays';
import DustMotes from './DustMotes';
import mediaService from '../../../services/mediaService';

interface AnimatedBackgroundProps {
  houseId: string;
}

export default function AnimatedBackground({ houseId }: AnimatedBackgroundProps) {
  const { getHouse, getMascotUserId } = useApp();
  const house = getHouse(houseId);

  const backgroundGradients: Record<string, string> = {
    house1: 'radial-gradient(circle at top, #f8fafc 0%, #94a3b8 45%, #0f172a 100%)',
    house2: 'radial-gradient(circle at top, #fef08a 0%, #f59e0b 45%, #78350f 100%)',
    house3: 'radial-gradient(circle at top, #fecaca 0%, #ef4444 45%, #450a0a 100%)',
    house4: 'radial-gradient(circle at top, #bfdbfe 0%, #3b82f6 45%, #1e3a8a 100%)',
    house5: 'radial-gradient(circle at top, #bbf7d0 0%, #22c55e 45%, #052e16 100%)',
  };

  const fallbackBackgrounds: Record<string, string> = {
    house1: '/1.png',
    house2: '/2.png',
    house3: '/3.png',
    house4: '/4.png',
    house5: '/1.png',
  };

  const resolvedMascotId = getMascotUserId(houseId);
  if (!resolvedMascotId) {
    return null;
  }
  const bgImage = (house?.backgroundImage?.startsWith('https://') 
                          ? house?.backgroundImage
                          : mediaService.getDirectUrl(house?.id || '', String(house?.backgroundImage))) || fallbackBackgrounds[houseId] || '/1.png';
  const bgGradient = backgroundGradients[houseId] || backgroundGradients.house1;

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden bg-gray-900">
      <AnimatePresence mode="popLayout">
        <motion.div
          key={houseId}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full"
          style={{ backgroundImage: bgGradient }}
        />
        <motion.img
          key={`${houseId}-image`}
          src={bgImage}
          alt="Background"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {/* Darken Overlay */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px] z-0" />

      {/* Cinematic Effects */}
      <GodRays houseId={houseId} />
      <DustMotes />

      {/* Vignette */}
      <div className="absolute inset-0 z-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.6) 100%)' }}
      />
    </div>
  );
}
